
import React from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useLanguage } from '@/contexts/LanguageContext';

const ContactFAQ: React.FC = () => {
  const { currentLanguage } = useLanguage();
  
  // FAQ questions based on language
  const faqs = {
    de: [
      {
        question: "Wie schnell erhalte ich eine Antwort auf meine Anfrage?",
        answer: "Wir antworten auf alle Anfragen innerhalb von 24 Stunden an Werktagen. In vielen Fällen erhalten Sie sogar bereits innerhalb weniger Stunden eine erste Rückmeldung von unserem Team."
      },
      {
        question: "Können wir ein persönliches Beratungsgespräch vereinbaren?",
        answer: "Selbstverständlich! Wir bieten sowohl virtuelle als auch persönliche Beratungsgespräche an. Teilen Sie uns einfach in Ihrer Nachricht mit, welche Art von Gespräch Sie bevorzugen, und wir werden einen Termin vereinbaren."
      },
      {
        question: "Welche Informationen benötigen Sie für ein erstes Angebot?",
        answer: "Für ein erstes unverbindliches Angebot benötigen wir einige grundlegende Informationen zu Ihrem Projekt: Art des Videos, gewünschte Länge, Zielgruppe und Verwendungszweck. Je mehr Details Sie uns mitteilen können, desto genauer können wir unser Angebot gestalten."
      },
      {
        question: "Kann ich Beispiele für ähnliche Projekte sehen?",
        answer: "Ja, wir stellen gerne Referenzprojekte bereit, die mit Ihrem Vorhaben vergleichbar sind. Geben Sie einfach in Ihrer Anfrage an, für welche Branche oder Art von Videos Sie sich besonders interessieren."
      }
    ],
    en: [
      {
        question: "How quickly will I receive a response to my inquiry?",
        answer: "We respond to all inquiries within 24 hours on business days. In many cases, you'll receive an initial response from our team within just a few hours."
      },
      {
        question: "Can we arrange a personal consultation?",
        answer: "Absolutely! We offer both virtual and in-person consultations. Simply let us know in your message which type of meeting you prefer, and we'll arrange an appointment."
      },
      {
        question: "What information do you need for an initial quote?",
        answer: "For an initial no-obligation quote, we need some basic information about your project: type of video, desired length, target audience, and purpose. The more details you can provide, the more accurately we can tailor our offer."
      },
      {
        question: "Can I see examples of similar projects?",
        answer: "Yes, we're happy to provide reference projects that are comparable to your project. Just indicate in your inquiry which industry or type of videos you're particularly interested in."
      }
    ],
    fr: [
      {
        question: "Dans quel délai recevrai-je une réponse à ma demande ?",
        answer: "Nous répondons à toutes les demandes dans un délai de 24 heures les jours ouvrables. Dans de nombreux cas, vous recevrez une première réponse de notre équipe en quelques heures seulement."
      },
      {
        question: "Pouvons-nous organiser une consultation personnelle ?",
        answer: "Absolument ! Nous proposons des consultations virtuelles et en personne. Il vous suffit de nous indiquer dans votre message le type de réunion que vous préférez, et nous organiserons un rendez-vous."
      },
      {
        question: "Quelles informations avez-vous besoin pour un devis initial ?",
        answer: "Pour un devis initial sans engagement, nous avons besoin de quelques informations de base sur votre projet : type de vidéo, durée souhaitée, public cible et objectif. Plus vous pourrez nous fournir de détails, plus nous pourrons adapter précisément notre offre."
      },
      {
        question: "Puis-je voir des exemples de projets similaires ?",
        answer: "Oui, nous sommes heureux de vous fournir des projets de référence comparables à votre projet. Indiquez simplement dans votre demande l'industrie ou le type de vidéos qui vous intéresse particulièrement."
      }
    ],
    it: [
      {
        question: "Quanto velocemente riceverò una risposta alla mia richiesta?",
        answer: "Rispondiamo a tutte le richieste entro 24 ore nei giorni lavorativi. In molti casi, riceverai una risposta iniziale dal nostro team in poche ore."
      },
      {
        question: "Possiamo organizzare una consulenza personale?",
        answer: "Assolutamente! Offriamo consulenze sia virtuali che di persona. Basta farci sapere nel tuo messaggio quale tipo di incontro preferisci e organizzeremo un appuntamento."
      },
      {
        question: "Di quali informazioni avete bisogno per un preventivo iniziale?",
        answer: "Per un preventivo iniziale senza impegno, abbiamo bisogno di alcune informazioni di base sul tuo progetto: tipo di video, durata desiderata, pubblico target e scopo. Più dettagli puoi fornire, più accuratamente potremo personalizzare la nostra offerta."
      },
      {
        question: "Posso vedere esempi di progetti simili?",
        answer: "Sì, siamo felici di fornirti progetti di riferimento comparabili al tuo progetto. Indica semplicemente nella tua richiesta quale settore o tipo di video ti interessa particolarmente."
      }
    ]
  };

  const getCurrentFAQs = () => {
    return faqs[currentLanguage as keyof typeof faqs] || faqs.en;
  };

  return (
    <div className="w-full max-w-3xl mx-auto mt-16 mb-8">
      <h3 className="text-2xl font-bold text-white mb-6 text-center font-future">
        {currentLanguage === 'de' ? 'Häufig gestellte Fragen' :
         currentLanguage === 'fr' ? 'Questions fréquentes' :
         currentLanguage === 'it' ? 'Domande frequenti' :
         'Frequently Asked Questions'}
      </h3>
      
      <div className="bg-black/40 backdrop-blur-sm border border-purple-500/20 rounded-xl p-6 transition-all duration-300 hover:border-purple-500/40">
        <Accordion type="single" collapsible className="w-full">
          {getCurrentFAQs().map((faq, index) => (
            <AccordionItem 
              key={index} 
              value={`faq-${index}`}
              className="border-b border-gray-800 last:border-0"
            >
              <AccordionTrigger className="text-gray-200 hover:text-purple-400 py-4 transition-all duration-300">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="text-gray-400 animate-accordion-down">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  );
};

export default ContactFAQ;
